/**
 * Cache for storing precise datetime values from API responses
 * Maps timestamp (milliseconds) to an object with start and end ISO strings (with microseconds)
 * This allows us to preserve the exact datetime from the API even after
 * converting to JavaScript Date objects which only have millisecond precision
 *
 * When duplicates are detected (same millisecond, different microseconds):
 * - start: ISO string with greater microseconds
 * - end: ISO string with lesser microseconds
 * When no duplicates:
 * - start and end are the same value
 *
 * The cache is persisted to localStorage under the key "minemaestro-precise"
 *
 * Memory Optimization Features:
 * - LRU eviction when cache exceeds max size
 * - TTL (Time To Live) for entries
 * - Limited duplicate attempts tracking
 * - Debounced localStorage writes
 */

const STORAGE_KEY = 'minemaestro-precise-v2';
const MAX_CACHE_SIZE = 10000; // Maximum number of entries
const MAX_DUPLICATE_ATTEMPTS = 100; // Maximum number of duplicate attempts to track
const ENTRY_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const STORAGE_SAVE_DEBOUNCE = 1000; // Debounce localStorage writes by 1 second

interface PreciseDateValue {
  start: string;
  end: string;
  accessedAt: number; // Timestamp of last access (for LRU)
  createdAt: number; // Timestamp of creation (for TTL)
  eventLayer?: string; // Event layer this timestamp belongs to (e.g., 'dig' or 'truck')
}

class PreciseDateCache {
  private cache: Map<number, PreciseDateValue> = new Map();
  private duplicateAttempts: Array<{ timestamp: number; existing: string; attempted: string; attemptedAt: number }> = [];
  private saveTimeout: NodeJS.Timeout | null = null;

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Load cache from localStorage and clean up expired entries
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        const now = Date.now();

        // Load entries and filter out expired ones
        for (const [key, value] of Object.entries(data)) {
          const timestamp = Number(key);
          const entry = value as PreciseDateValue;

          // Check if entry has expired (TTL check)
          if (entry.createdAt && (now - entry.createdAt) < ENTRY_TTL) {
            this.cache.set(timestamp, entry);
          }
          // Skip expired entries - they won't be loaded
        }
      }
    } catch {
      // Failed to load from localStorage
    }
  }

  /**
   * Save cache to localStorage (debounced)
   */
  private saveToStorage(): void {
    // Clear existing timeout
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    // Schedule a new save
    this.saveTimeout = setTimeout(() => {
      try {
        const data = Object.fromEntries(this.cache.entries());
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        this.saveTimeout = null;
      } catch {
        // Failed to save to localStorage
        this.saveTimeout = null;
      }
    }, STORAGE_SAVE_DEBOUNCE);
  }

  /**
   * Force immediate save to localStorage (bypassing debounce)
   */
  private saveToStorageImmediate(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }

    try {
      const data = Object.fromEntries(this.cache.entries());
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Failed to save to localStorage
    }
  }

  /**
   * Store a precise datetime mapping
   * @param timestamp - The timestamp in milliseconds
   * @param isoString - The original ISO string with microseconds (e.g., "2025-07-16T14:00:37.622485Z")
   */
  set(timestamp: number, isoString: string): void {
    const now = Date.now();

    // Check if we're replacing an existing value
    const existingValue = this.cache.get(timestamp);

    if (existingValue) {
      // Check if this ISO string is already stored
      if (existingValue.start === isoString || existingValue.end === isoString) {
        // Update access time for LRU
        existingValue.accessedAt = now;
        return;
      }

      // We have a duplicate timestamp with different microseconds
      // Track this duplicate attempt (with size limit)
      this.duplicateAttempts.push({
        timestamp,
        existing: existingValue.start === existingValue.end ? existingValue.start : `${existingValue.start} / ${existingValue.end}`,
        attempted: isoString,
        attemptedAt: now
      });

      // Keep only the most recent duplicate attempts
      if (this.duplicateAttempts.length > MAX_DUPLICATE_ATTEMPTS) {
        this.duplicateAttempts = this.duplicateAttempts.slice(-MAX_DUPLICATE_ATTEMPTS);
      }

      // Compare microseconds to determine which should be start (greater) and which should be end (lesser)
      const existingMicro = this.extractMicroseconds(existingValue.start);
      const newMicro = this.extractMicroseconds(isoString);

      let start: string;
      let end: string;

      if (newMicro > existingMicro) {
        // New value has greater microseconds - it becomes start
        start = isoString;
        end = existingValue.start; // Use existing start as the baseline
      } else {
        // Existing value has greater microseconds - keep it as start
        start = existingValue.start;
        end = isoString;
      }

      // Update the cache with both values
      this.cache.set(timestamp, {
        start,
        end,
        accessedAt: now,
        createdAt: existingValue.createdAt
      });
      this.saveToStorage();
    } else {
      // New entry - both start and end are the same
      const newValue: PreciseDateValue = {
        start: isoString,
        end: isoString,
        accessedAt: now,
        createdAt: now
      };
      this.cache.set(timestamp, newValue);

      // Check cache size and evict if necessary
      this.evictIfNecessary();

      this.saveToStorage();
    }
  }

  /**
   * Batch set multiple precise datetime mappings (more efficient than multiple set() calls)
   * @param entries - Array of [timestamp, isoString, eventLayer?] tuples
   */
  setBatch(entries: Array<[number, string, string?]>): void {
    const now = Date.now();

    for (const [timestamp, isoString, eventLayer] of entries) {
      const existingValue = this.cache.get(timestamp);

      if (eventLayer === 'truck') {
        // Truck entries: never store their ISO strings in the cache.
        // Only mark collision if a dig entry already exists at this millisecond.
        if (existingValue) {
          if (existingValue.eventLayer === 'dig') {
            existingValue.eventLayer = 'dig+truck';
          }
        } else {
          // No dig entry yet — store a placeholder so if a dig entry arrives later,
          // we can detect the collision
          this.cache.set(timestamp, {
            start: isoString,
            end: isoString,
            accessedAt: now,
            createdAt: now,
            eventLayer: 'truck',
          });
        }
        continue;
      }

      // From here on, eventLayer is 'dig' (or undefined)
      if (existingValue) {
        // If the existing entry was a truck placeholder, replace it with dig data
        // and mark the collision
        if (existingValue.eventLayer === 'truck') {
          this.cache.set(timestamp, {
            start: isoString,
            end: isoString,
            accessedAt: now,
            createdAt: now,
            eventLayer: 'dig+truck',
          });
          continue;
        }

        // Check if this ISO string is already stored
        if (existingValue.start === isoString || existingValue.end === isoString) {
          existingValue.accessedAt = now;
          if (eventLayer) existingValue.eventLayer = eventLayer;
          continue;
        }

        // Handle duplicate with different microseconds (both are dig events)
        const existingMicro = this.extractMicroseconds(existingValue.start);
        const newMicro = this.extractMicroseconds(isoString);

        const start = newMicro > existingMicro ? isoString : existingValue.start;
        const end = newMicro > existingMicro ? existingValue.start : isoString;

        this.cache.set(timestamp, {
          start,
          end,
          accessedAt: now,
          createdAt: existingValue.createdAt,
          eventLayer: eventLayer || existingValue.eventLayer,
        });
      } else {
        // New dig entry
        this.cache.set(timestamp, {
          start: isoString,
          end: isoString,
          accessedAt: now,
          createdAt: now,
          eventLayer,
        });
      }
    }

    // Check cache size once after all entries are added
    this.evictIfNecessary();

    // Save to storage once after batch operation
    this.saveToStorage();
  }

  /**
   * Set the event layer for an existing cache entry.
   * Used to tag timestamps with their source layer (e.g., 'dig' or 'truck')
   * for debugging which layer a cached timestamp belongs to.
   * @param timestamp - The timestamp in milliseconds
   * @param eventLayer - The event layer (e.g., 'dig', 'truck')
   */
  setEventLayer(timestamp: number, eventLayer: string): void {
    const value = this.cache.get(timestamp);
    if (value) {
      // Only set if not already tagged, or if upgrading from truck to dig
      // (dig takes priority since it's the valid layer for cycle boundaries)
      if (!value.eventLayer || (value.eventLayer === 'truck' && eventLayer === 'dig')) {
        value.eventLayer = eventLayer;
      } else if (value.eventLayer === 'dig' && eventLayer === 'truck') {
        // Both layers share this timestamp — mark as collision for debugging
        value.eventLayer = 'dig+truck';
      } else if (value.eventLayer !== eventLayer && !value.eventLayer.includes('+')) {
        value.eventLayer = `${value.eventLayer}+${eventLayer}`;
      }
      this.saveToStorage();
    }
  }

  /**
   * Evict least recently used entries if cache exceeds max size
   */
  private evictIfNecessary(): void {
    if (this.cache.size <= MAX_CACHE_SIZE) {
      return;
    }

    // Calculate how many entries to remove (remove 10% when limit is exceeded)
    const entriesToRemove = Math.ceil(MAX_CACHE_SIZE * 0.1);

    // Sort entries by accessedAt (oldest first)
    const sortedEntries = Array.from(this.cache.entries())
      .sort((a, b) => a[1].accessedAt - b[1].accessedAt);

    // Remove the oldest entries
    for (let i = 0; i < entriesToRemove; i++) {
      this.cache.delete(sortedEntries[i][0]);
    }
  }

  /**
   * Helper to extract microseconds as a number from an ISO string
   */
  private extractMicroseconds(isoString: string): number {
    const microStr = isoString.split('.')[1]?.split('Z')[0] || '0';
    return parseInt(microStr, 10);
  }

  /**
   * Helper to extract microsecond difference between two ISO strings
   */
  private getMicrosecondDifference(iso1: string, iso2: string): string {
    const micro1 = iso1.split('.')[1]?.split('Z')[0] || '0';
    const micro2 = iso2.split('.')[1]?.split('Z')[0] || '0';
    return `${micro1} vs ${micro2}`;
  }

  /**
   * Retrieve the precise datetime value for a timestamp
   * @param timestamp - The timestamp in milliseconds
   * @returns The PreciseDateValue object with start and end, or null if not found
   */
  get(timestamp: number): PreciseDateValue | null {
    const value = this.cache.get(timestamp);
    if (value) {
      // Update access time for LRU
      value.accessedAt = Date.now();
    }
    return value || null;
  }

  /**
   * Retrieve the start ISO string for a timestamp (greater microseconds)
   * @param timestamp - The timestamp in milliseconds
   * @returns The start ISO string, or null if not found
   */
  getStart(timestamp: number): string | null {
    const value = this.cache.get(timestamp);
    if (value) {
      // Update access time for LRU
      value.accessedAt = Date.now();
      return value.start;
    }
    return null;
  }

  /**
   * Retrieve the end ISO string for a timestamp (lesser microseconds)
   * @param timestamp - The timestamp in milliseconds
   * @returns The end ISO string, or null if not found
   */
  getEnd(timestamp: number): string | null {
    const value = this.cache.get(timestamp);
    if (value) {
      // Update access time for LRU
      value.accessedAt = Date.now();
      return value.end;
    }
    return null;
  }

  /**
   * Check if a timestamp has a precise datetime stored
   * @param timestamp - The timestamp in milliseconds
   */
  has(timestamp: number): boolean {
    return this.cache.has(timestamp);
  }

  /**
   * Clear all cached precise datetimes
   */
  clear(): void {
    // Clear any pending save operations
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }

    this.cache.clear();
    this.duplicateAttempts = [];
    localStorage.removeItem(STORAGE_KEY);
  }

  /**
   * Clean up expired entries based on TTL
   * Returns the number of entries removed
   */
  cleanupExpired(): number {
    const now = Date.now();
    let removed = 0;

    for (const [timestamp, value] of this.cache.entries()) {
      if (now - value.createdAt >= ENTRY_TTL) {
        this.cache.delete(timestamp);
        removed++;
      }
    }

    if (removed > 0) {
      this.saveToStorageImmediate();
    }

    return removed;
  }

  /**
   * Get the size of the cache
   */
  get size(): number {
    return this.cache.size;
  }

  /**
   * Find potential duplicate timestamps (same timestamp, different ISO strings)
   * Returns array of all duplicate attempts that were rejected
   */
  getDuplicateAttempts(): Array<{ timestamp: number; existing: string; attempted: string; attemptedAt: number }> {
    return this.duplicateAttempts;
  }

  /**
   * Get duplicate attempts count
   */
  getDuplicateCount(): number {
    return this.duplicateAttempts.length;
  }

  /**
   * Clear duplicate attempts history
   */
  clearDuplicateAttempts(): void {
    this.duplicateAttempts = [];
  }

  /**
   * Get all entries in the cache (for debugging)
   */
  getAllEntries(): Array<{ timestamp: number; start: string; end: string; date: string; hasDuplicate: boolean; eventLayer?: string }> {
    return Array.from(this.cache.entries()).map(([timestamp, value]) => ({
      timestamp,
      start: value.start,
      end: value.end,
      date: new Date(timestamp).toISOString(),
      hasDuplicate: value.start !== value.end,
      eventLayer: value.eventLayer,
    }));
  }

  /**
   * Check for potential timestamp collisions
   * Returns entries where multiple microsecond-precise times map to the same millisecond timestamp
   */
  checkForCollisions(): void {
    // Collisions are prevented by keeping the first value when duplicates are detected
  }
}

// Export a singleton instance
export const preciseDateCache = new PreciseDateCache();
